import { makeStyles } from '@material-ui/core';
import { memo, useEffect, useRef, useState } from 'react';
import mentorsAPI from '../../../api/mentors';
import venturesAPI from '../../../api/ventures';
import { COLORS, MONTSERRAT_FONT_FAMILY } from '../../../theme/variables';

export interface TooltipData {
  type: 'mentor' | 'venture';
  name: string;
  entityId?: string;
  fields: Array<{ label: string; value: string }>;
}

interface HeatmapTooltipProps {
  anchorEl: HTMLElement | null;
  data: TooltipData | null;
}

const useStyles = makeStyles({
  root: {
    position: 'fixed',
    zIndex: 1500,
    pointerEvents: 'none',
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
    borderRadius: 8,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
    padding: '12px 16px',
    maxWidth: 280,
    minWidth: 180,
  },
  name: {
    fontFamily: MONTSERRAT_FONT_FAMILY,
    fontWeight: 700,
    fontSize: 14,
    color: COLORS.COLOR_TEXT_BASE,
    marginBottom: 6,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
    marginBottom: 2,
    marginTop: 6,
  },
  fieldValue: {
    fontSize: 13,
    color: COLORS.COLOR_TEXT_LIGHTENED_10,
    lineHeight: 1.4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical' as const,
  },
});

const HeatmapTooltip = memo(function HeatmapTooltip({
  anchorEl,
  data,
}: HeatmapTooltipProps) {
  const classes = useStyles();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: -9999,
    left: -9999,
  });
  const [fetchedFields, setFetchedFields] = useState<Record<
    string,
    string
  > | null>(null);

  // Delayed fetch for fresh entity data
  useEffect(() => {
    // Reset position so stale pos from previous chip doesn't cause flicker
    setPos({ top: -9999, left: -9999 });

    if (!data?.entityId) {
      setFetchedFields(null);
      return;
    }

    let cancelled = false;
    setFetchedFields(null);

    const timer = setTimeout(async () => {
      if (cancelled) return;

      try {
        if (data.type === 'mentor') {
          const [mentor, specs] = await Promise.all([
            mentorsAPI.getMentor(data.entityId as any),
            mentorsAPI.getMentorSpecializations(data.entityId as any),
          ]);
          if (cancelled) return;
          setFetchedFields({
            Specialization: specs.map((s) => s.name).join(', '),
            About: mentor.about || '',
          });
        } else {
          const venture = await venturesAPI.getVenture(data.entityId as any);
          if (cancelled) return;
          setFetchedFields({
            Stage: venture.ventureStage || '',
            Description: venture.description || '',
          });
        }
      } catch {
        // Silently fail — tooltip stays hidden
      }
    }, 750);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [data?.entityId, data?.type]);

  useEffect(() => {
    if (!anchorEl || !ref.current) return;
    // Guard against detached elements or zero-size rects
    if (!anchorEl.isConnected) return;
    const anchorRect = anchorEl.getBoundingClientRect();
    if (anchorRect.width === 0 && anchorRect.height === 0) return;

    const tooltipRect = ref.current.getBoundingClientRect();

    let top = anchorRect.top - tooltipRect.height - 8;
    let left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;

    // Flip below if no space above
    if (top < 8) {
      top = anchorRect.bottom + 8;
    }
    // Clamp horizontal
    left = Math.max(
      8,
      Math.min(left, window.innerWidth - tooltipRect.width - 8),
    );

    setPos({ top, left });
  }, [anchorEl, data, fetchedFields]);

  // Don't render until the backend call has completed
  if (!data || !anchorEl || !fetchedFields || !anchorEl.isConnected) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={classes.root}
      style={{ top: pos.top, left: pos.left }}
    >
      <div className={classes.name}>{data.name}</div>
      {data.fields.map((f) => {
        const value =
          f.label in fetchedFields ? fetchedFields[f.label] : f.value;
        return (
          <div key={f.label}>
            <div className={classes.fieldLabel}>{f.label}</div>
            <div className={classes.fieldValue}>{value || '—'}</div>
          </div>
        );
      })}
    </div>
  );
});

export default HeatmapTooltip;
